import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Grid2,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { MdOutlineArrowOutward } from "react-icons/md";
import moment from "moment";
import { useGetConstructionProgressKpiQuery } from "store/slices/apis/constructionDashboardSlice ";
import { useParams } from "react-router-dom";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import ProjectStatusOverviewHead from "components/ProjectStatusOverviewHead";
import { ReactComponent as ArrowIcon } from "assets/PwC_Funct_Icons_ArrowIncrease_Outline_Black_RGB.svg"
import { ReactComponent as ArrowUpIcon } from "assets/PwC_Funct_Icons_ArrowUp_Outline_Black_RGB.svg"
import { ReactComponent as ArrowDownIcon } from "assets/PwC_Funct_Icons_ArrowDown_Outline_Black_RGB.svg"

const ProgressKpiConstruction = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { data, isLoading } = useGetConstructionProgressKpiQuery({ projectId });
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);

  const lastUpdated = data?.lastUpdated;

  if (isLoading) {
    return (
      <Box>
        <Grid2 container spacing={5} sx={{ mt: "4px" }}>
          <ProjectStatusOverviewHead
            lastModified={lastUpdated}
            marginBottm={"10px"}
          />
        </Grid2>
        <Grid2 container spacing={5} sx={{ mt: "20px" }}>
          <Grid2 size={12}>
            <Skeleton width="30%" height={30} sx={{ mb: 1 }} />
            <Skeleton width="60%" height={24} />
          </Grid2>
        </Grid2>

        <Grid2 container sx={{ mt: "10px", mx: "-10px", rowGap: "10px" }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid2 key={index} size={{ xs: 12, md: 3 }} sx={{ px: "10px" }}>
              <Card
                sx={{
                  boxShadow: "none",
                  border: "1px solid #25252517",
                  borderRadius: "3px",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                >
                  <Box display="flex" alignItems="center">
                    <Skeleton
                      variant="rectangular"
                      width={40}
                      height={40}
                      sx={{ mr: 2, borderRadius: 1 }}
                    />
                    <Box>
                      <Skeleton
                        variant="text"
                        width={70}
                        height={24}
                        sx={{ mb: 0.5 }}
                      />
                      <Skeleton variant="text" width={120} height={20} />
                    </Box>
                  </Box>
                  <Skeleton variant="circular" width={37} height={37} />
                </Box>
              </Card>
            </Grid2>
          ))}
        </Grid2>
      </Box>
    );
  }

  const progressCards = [
    {
      title: "Total Critical Activities",
      value: isLoading ? "--" : data?.totalCriticalActivities ?? 0,
      change: isLoading ? "" : data?.totalCriticalActivitiesChange ?? 0,
      //   isPositive: isLoading
      //     ? ""
      //     : parseFloat(data?.totalCriticalActivitiesChange) >= 0,
      dotColor: "#377E36",
      color: "#CDFBE7",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
      timeFrame: "this month",
      valueColor: "#252525",
      hasChange: true,
    },
    {
      title: "Delay in Days",
      value: isLoading ? "--" : data?.delayInDays ?? 0,
      change: isLoading ? "" : data?.delayInDaysChange ?? 0,
      //   isPositive: isLoading ? "" : parseFloat(data?.delayInDaysChange) >= 0,
      dotColor: "#377E36",
      color: "#CDFBE7",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
      timeFrame: "this month",
      valueColor: "#252525",
      hasChange: false,
    },
    {
      title: "Critical Activities Variance",
      value: isLoading ? "--" : data?.criticalPathVariance ?? 0,
      change: isLoading ? "" : data?.criticalPathVarianceChange ?? 0,
      //   isPositive: isLoading
      //     ? ""
      //     : parseFloat(data?.criticalPathVarianceChange) >= 0,
      dotColor: "#377E36",
      color: "#CDFBE7",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
      valueColor: "#252525",
      timeFrame: "this month",
      hasChange: false,
    },
    {
      title: "Schedule Variance",
      value: isLoading ? "--" : `${data?.scheduleVariancePercent ?? 0}%`,
      dotColor: "#377E36",
      color: "#9ED3CC",
      valueColor: "#252525",
      hasChange: false,
      iconColor: "#E0F0E4",
      textColor: "#377E36",
      timeFrame: "this month",
      dotColor: "#CE412D",
      hasChange: false,
    },
    {
      title: "Forecast Completion Date",
      value: data?.forecastCompletionDate
        ? moment(data?.forecastCompletionDate).format("D MMMM, YYYY")
        : "--",
      isDate: true,
      valueColor: "#CB402D",
      iconColor: "#E0F0E4",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
      hasChange: false,
    },
    {
      title: "Planned Completion Date",
      value: data?.plannedCompletionDate
        ? moment(data?.plannedCompletionDate).format("D MMMM, YYYY")
        : "--",
      isDate: true,
      iconColor: "#E0F0E4",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
      valueColor: "#252525",
      hasChange: false,
    },
  ];

  const cardsDataKpi = progressCards?.map((item) => {
    const changeValue = item.change;
    const arrow = changeValue >= 0 ? "up" : "down";
    const isPositive = changeValue > 0 ? true : false;
    const absoluteChange = isNaN(changeValue)
      ? "-"
      : changeValue === -100
        ? "0%"
        : `${Math.abs(changeValue)}%`;
    const redColor = "#CE412D";
    return {
      ...item,
      arrow,
      absoluteChange,
      isPositive,
      redColor,
    };
  });

  return (
    <Box>
      <Grid2 container sx={{ mt: "4px" }}>
        <ProjectStatusOverviewHead
          lastModified={lastUpdated}
          marginBottm={"10px"}
        />
      </Grid2>

      <Grid2 container columnSpacing={3} rowSpacing={1.25} sx={{ mt: "20px" }}>
        {cardsDataKpi?.map((item, index) => (
          <Grid2 key={index} size={{ xs: 12, md: 3 }}>
            <Card
              sx={{
                boxShadow: "none",
                border: "1px solid #25252517",
                borderRadius: "3px",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={2}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    sx={{ bgcolor: item.color, width: 45, height: 45, mr: 2 }}
                  />
                  <Box>
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mr: 1,
                          color: item.valueColor,
                          fontSize: "18px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.value}
                      </Typography>
                      {item.hasChange && (
                        <Chip
                          label={item.absoluteChange}
                          size="small"
                          sx={{
                            backgroundColor: item.iconColor,
                            color: item?.isPositive
                              ? item?.redColor
                              : item.textColor,
                            fontWeight: 600,
                            fontSize: "12px",
                            borderRadius: "4px",
                            // width: "45px",
                            height: "20px",
                          }}
                          icon={
                            item?.absoluteChange !== "0%" &&
                            // (item.arrow === "up" ? (
                            //   <ArrowUpward
                            //     sx={{
                            //       fontSize: "12px !important",
                            //       color: !item.isPositive
                            //         ? `${item.changeColor} !important`
                            //         : `${item?.redColor} !important`,
                            //     }}
                            //   />
                            // ) : (
                            //   <ArrowDownward
                            //     sx={{
                            //       fontSize: "12px !important",
                            //       color: !item.isPositive
                            //         ? `${item.changeColor} !important`
                            //         : `${item.redColor} !important`,
                            //     }}
                            //   />
                            // ))
                            (
                              item.arrow === "up" ? (
                                <ArrowUpIcon
                                  style={{
                                    height: "12px",
                                    width: "12px",
                                    fill: !item.isPositive ? item.changeColor : item.redColor,
                                  }}
                                />
                              ) : (
                                <ArrowDownIcon
                                  style={{
                                    height: "12px",
                                    width: "12px",
                                    fill: !item.isPositive ? item.changeColor : item.redColor,
                                  }}
                                />
                              )
                            )

                          }
                        />
                      )}
                      <Typography
                        variant="body2"
                        color="#474747"
                        sx={{
                          ml: "2px",
                          fontWeight: 400,
                          fontSize: "13px",
                          lineHeight: "24px",
                          letterSpacing: "-0.4px",
                          color: "#474747",
                        }}
                      >
                        {item.timeFrame}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: "5px",
                        lineHeight: "100%",
                        letterSpacing: "-0.4px",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#252525",
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Box>
                {item.hasChange && (
                  <Avatar
                    sx={{
                      bgcolor: "#fff",
                      width: "37px",
                      height: "37px",
                      border: "1px solid #d3d3d3",
                    }}
                  >
                    {/* <MdOutlineArrowOutward
                      style={{
                        color: "#252525",
                        fontSize: "20px",
                        cursor: "pointer",
                        
                      }}
                      onClick={() => {
                        setPopupData(item);
                        setOpen(true);
                      }}
                    /> */}

                    <ArrowIcon
                      style={{
                        color: "#252525",
                        fontSize: "13px",
                        cursor: "pointer",
                        height: "13px",
                        stroke: "#252525",
                        strokeWidth: "6"
                      }}
                      onClick={() => {
                        const el = document.getElementById("construction-variance-drivers");
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }}
                    />
                  </Avatar>
                )}
              </Box>
            </Card>
          </Grid2>
        ))}
      </Grid2>
    </Box>
  );
};

export default ProgressKpiConstruction;
